import React, { useEffect, useState } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import { VerticalLine } from "../vertical-line/VerticalLine";
import ControlGroup from "@amzn/meridian/control-group";
import { Space } from "../space/Space";
import { tableMapping } from "./definitions";
import Checkbox from "@amzn/meridian/checkbox";
import { pull } from "lodash";
import RadioButton from "@amzn/meridian/radio-button";
import { getAllPossibleColumns } from "./utils";

export interface ITablePreferenceModalProps {
    openPreferenceModal: boolean;
    setOpenPreferenceModal: (value) => void;
    preferences: any;
    setPreferences: (value) => void;
    tableName: string;
}

export const TablePreferencesModal: React.FC<ITablePreferenceModalProps> = ({
    openPreferenceModal,
    setOpenPreferenceModal,
    preferences,
    setPreferences,
    tableName,
}) => {
    const tableColumns = getAllPossibleColumns(tableName);
    const [showDividers, setShowDividers] = useState(preferences?.showDividers ?? false);
    const [tableSpacing, setTableSpacing] = useState(preferences?.tableSpacing ?? "small");
    const [showStripes, setShowStripes] = useState(preferences?.showStripes ?? false);
    const [highlightOnHover, setHighlightOnHover] = useState(
        preferences?.highlightOnHover ?? false
    );

    const [visibleColumns, setVisibleColumns] = useState(
        pull(
            preferences.allTables[tableName]?.map(column => {
                if (column.visible) {
                    return column.subId ? column.subId : column.id;
                }
            }),
            undefined
        )
    );

    const OnConfirmClick = () => {
        setOpenPreferenceModal(false);

        const tablePreferences = preferences.allTables[tableName].map(column => {
            if (column.subId) {
                return {
                    id: column.id,
                    subId: column.subId,
                    visible: visibleColumns.includes(column.subId),
                };
            }

            return { id: column.id, visible: visibleColumns.includes(column.id) };
        });
        setPreferences({
            allTables: {
                ...preferences.allTables,
                [tableName]: tablePreferences,
            },
            showDividers: showDividers,
            tableSpacing: tableSpacing,
            showStripes: showStripes,
            highlightOnHover: highlightOnHover,
        });
    };
    const reset = () => {
        setVisibleColumns(
            pull(
                preferences.allTables[tableName]?.map(column => {
                    if (column.visible) {
                        return column.subId ? column.subId : column.id;
                    }
                }),
                undefined
            )
        );
        setShowStripes(preferences.showStripes);
        setShowDividers(preferences.showDividers);
        setTableSpacing(preferences.tableSpacing);
        setHighlightOnHover(preferences.highlightOnHover);
    };
    const OnCancelOrClose = () => {
        reset();
        setOpenPreferenceModal(false);
    };

    useEffect(() => {
        reset();
    }, [tableName, preferences]);

    return (
        <React.Fragment>
            <Modal
                title="Preferences"
                open={openPreferenceModal}
                onClose={OnCancelOrClose}
                scrollContainer="viewport"
                closeLabel="Close"
                aria-describedby="modal-description"
            >
                <Row alignmentVertical={"top"}>
                    <Column spacing={"none"}>
                        <Column spacing={"none"}>
                            <Text type={"h200"} id="modal-description">
                                Table Spacing
                            </Text>
                            <Text id="modal-description">
                                Adjust the amount of whitespace within columns and rows.
                            </Text>
                            <Space size={"xs"} />

                            <Column width={"500px"} maxHeight={"300px"} overflowX={"scroll"}>
                                <ControlGroup onChange={setTableSpacing} value={tableSpacing}>
                                    <RadioButton key={"small"} value={"small"}>
                                        Small
                                    </RadioButton>
                                    <RadioButton key={"medium"} value={"medium"}>
                                        Medium
                                    </RadioButton>
                                </ControlGroup>
                            </Column>
                        </Column>
                        <Space size={"s"} />
                        <Column spacing={"none"}>
                            <Text type={"h200"} id="modal-description">
                                Highlight rows
                            </Text>
                            <Text id="modal-description">Highlight row when hovering over</Text>
                            <Space size={"xs"} />

                            <Checkbox
                                key={"highlight-on-hover"}
                                checked={highlightOnHover}
                                onChange={setHighlightOnHover}
                            >
                                Highlight on hover
                            </Checkbox>
                        </Column>
                        <Space size={"s"} />
                        <Column spacing={"none"}>
                            <Text type={"h200"} id="modal-description">
                                Show strips
                            </Text>
                            <Text id="modal-description">
                                Add background shading to alternating rows
                            </Text>
                            <Space size={"xs"} />

                            <Checkbox
                                key={"show-stripes"}
                                checked={showStripes}
                                onChange={setShowStripes}
                            >
                                Show strips
                            </Checkbox>
                        </Column>
                        <Space size={"s"} />
                        <Column spacing={"none"}>
                            <Text type={"h200"} id="modal-description">
                                Show dividers
                            </Text>
                            <Text id="modal-description">Add dividers between rows</Text>
                            <Space size={"xs"} />

                            <Checkbox
                                key={"show-dividers"}
                                checked={showDividers}
                                onChange={setShowDividers}
                            >
                                Show dividers
                            </Checkbox>
                        </Column>
                    </Column>

                    <VerticalLine height={500} />
                    <Column spacing={"none"}>
                        <Row>
                            <Text type={"h200"} id="modal-description">
                                Visible Columns
                            </Text>

                            <Button
                                type="link"
                                size="small"
                                onClick={() =>
                                    setVisibleColumns(
                                        tableMapping[tableName].tableHeader.map(column => {
                                            if (column.subId) return column.subId;
                                            else return column.id;
                                        })
                                    )
                                }
                            >
                                Reset columns
                            </Button>
                        </Row>
                        <Text id="modal-description">
                            Show or hide columns by selecting or deselecting from the list below.
                        </Text>
                        <Space size={"xs"} />

                        <Column width={"500px"} maxHeight={"450px"} overflowX={"scroll"}>
                            <ControlGroup onChange={setVisibleColumns} value={visibleColumns}>
                                {tableColumns
                                    .sort((a, b) => a?.order - b?.order)
                                    .map(column => {
                                        if (column.subId) {
                                            return (
                                                <Checkbox key={column.subId} value={column.subId}>
                                                    {column.label}
                                                </Checkbox>
                                            );
                                        }

                                        return (
                                            <Checkbox key={column.id} value={column.id}>
                                                {column.label}
                                            </Checkbox>
                                        );
                                    })}
                            </ControlGroup>
                        </Column>
                    </Column>

                    <Space size={"l"} />
                </Row>

                <ModalFooter>
                    <Row alignmentHorizontal="end" widths="fit">
                        <Button type="secondary" size="small" onClick={OnCancelOrClose}>
                            Cancel
                        </Button>
                        <Button type="primary" size="small" onClick={OnConfirmClick}>
                            Confirm
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};
